/**
 * Diagram registry — maps blog slug → lazy React component.
 * Add an entry here each time a new diagram is generated.
 * Components are lazy-loaded so they only bundle when the post is visited.
 */
import { lazy, type ComponentType } from "react";

type LazyDiagram = ReturnType<typeof lazy<ComponentType>>;

export const DIAGRAM_REGISTRY: Record<string, LazyDiagram> = {
  "what-is-programmatic-seo": lazy(() => import("./WhatIsProgrammaticSeoDiagram")),
  "the-cost-of-inaction-what-happens-when-you-delay-seo-investment": lazy(() => import("./TheCostOfInactionWhatHappensWhenYouDelaySeoInvestmentDiagram")),
  "what-redirects-are-seo-friendly": lazy(() => import("./WhatRedirectsAreSeoFriendlyDiagram")),
  "are-ai-overviews-making-organic-clicks-disappear": lazy(() => import("./AreAiOverviewsMakingOrganicClicksDisappearDiagram")),
  "the-role-of-seo-in-driving-business-value-and-competitive-advantage": lazy(() => import("./TheRoleOfSeoInDrivingBusinessValueAndCompetitiveAdvantageDiagram")),
  "local-seo-ranking-factors-2026": lazy(() => import("./LocalSeoRankingFactors2026Diagram")),
  "does-technical-seo-require-coding": lazy(() => import("./DoesTechnicalSeoRequireCodingDiagram")),
  "generative-engine-optimization-growth-strategies-and-metrics-for-the-ai-era": lazy(() => import("./GenerativeEngineOptimizationGrowthStrategiesAndMetricsForTheAiEraDiagram")),
  "is-seo-important-for-every-business": lazy(() => import("./IsSeoImportantForEveryBusinessDiagram")),
  "is-traditional-seo-becoming-a-risk-in-the-ai-era": lazy(() => import("./IsTraditionalSeoBecomingARiskInTheAiEraDiagram")),
  "does-seo-really-work": lazy(() => import("./DoesSeoReallyWorkDiagram")),
  "ultimate-guide-to-keyword-research": lazy(() => import("./UltimateGuideToKeywordResearchDiagram")),
  "seo-budget-allocation-how-much-should-growth-stage-companies-invest": lazy(() => import("./SeoBudgetAllocationHowMuchShouldGrowthStageCompaniesInvestDiagram")),
  "why-is-technical-seo-important": lazy(() => import("./WhyIsTechnicalSeoImportantDiagram")),
  "seo-vs-geo-vs-aeo-whats-the-difference": lazy(() => import("./SeoVsGeoVsAeoWhatsTheDifferenceDiagram")),
  "what-is-topical-authority": lazy(() => import("./WhatIsTopicalAuthorityDiagram")),
  "how-to-use-ai-in-content-production": lazy(() => import("./HowToUseAiInContentProductionDiagram")),
  "digital-pr-for-link-building": lazy(() => import("./DigitalPrForLinkBuildingDiagram")),
  "how-to-optimise-google-business-profile-2026": lazy(() => import("./HowToOptimiseGoogleBusinessProfile2026Diagram")),
  "programmatic-seo-examples": lazy(() => import("./ProgrammaticSeoExamplesDiagram")),
  "content-seo-guide": lazy(() => import("./ContentSeoGuideDiagram")),
  "link-building-guide": lazy(() => import("./LinkBuildingGuideDiagram")),
  "programmatic-seo-guide": lazy(() => import("./ProgrammaticSeoGuideDiagram")),
  "local-seo-guide": lazy(() => import("./LocalSeoGuideDiagram")),
  "how-ai-search-engines-decide-what-to-cite": lazy(() => import("./HowAiSearchEnginesDecideWhatToCiteDiagram")),
  "how-to-write-content-ai-will-cite": lazy(() => import("./HowToWriteContentAiWillCiteDiagram")),
  "geo-strategy-vs-seo": lazy(() => import("./GeoStrategyVsSeoDiagram")),
  "ai-search-statistics-2026": lazy(() => import("./AiSearchStatistics2026Diagram")),
  "how-google-ai-overviews-work": lazy(() => import("./HowGoogleAiOverviewsWorkDiagram")),
  "optimise-for-perplexity-chatgpt-gemini": lazy(() => import("./OptimiseForPerplexityChatgptGeminiDiagram")),
  "ai-seo-geo-complete-guide": lazy(() => import("./AiSeoGeoCompleteGuideDiagram")),
  "what-is-llms-txt-file": lazy(() => import("./WhatIsLlmsTxtFileDiagram")),
  "entity-seo-knowledge-graph-authority": lazy(() => import("./EntitySeoKnowledgeGraphAuthorityDiagram")),
  "paid-vs-editorial-backlinks-what-actually-builds-authority": lazy(() => import("./PaidVsEditorialBacklinksWhatActuallyBuildsAuthorityDiagram")),
  "zapier-seo-teardown": lazy(() => import("./ZapierSeoTeardownDiagram")),
};

/**
 * Slugs that ship an interactive diagram.
 *
 * 37 posts carry a hand-built explainer with a click-to-expand lightbox, and
 * nothing outside the article said so: no badge on the card, no entry in the
 * table of contents, no way to find them. They are the one thing on this blog
 * that competitors do not have, so listing surfaces badge them and
 * /blog/diagrams collects them.
 *
 * Derived from the registry rather than written out, so the two cannot drift.
 * Reading this list loads no diagram: the registry holds lazy() wrappers, and
 * a component is only fetched when it renders.
 */
export const DIAGRAM_SLUGS: readonly string[] = Object.keys(DIAGRAM_REGISTRY);

/** True when a post has an interactive diagram. */
export function hasDiagram(slug: string): boolean {
  return Object.prototype.hasOwnProperty.call(DIAGRAM_REGISTRY, slug);
}

/** The in-page anchor DiagramWrapper gives a post's diagram. */
export function diagramAnchor(slug: string): string {
  return `dgm-${slug}`;
}
